import * as React from 'react'
import { useMemo, useState } from 'react'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import {
  ServiceAccount,
  ServiceAccountChild,
  useMutationAttachCompany,
  useMutationDetachCompany,
  useQueryAttachableCompanies,
  useQueryServiceAccounts,
} from '@/modules/shared/query-client/useServiceAccountAdmin'
import {
  ServiceAccountUsersPanel,
  Th,
  Td,
  billingProviderLabel,
  extractCode,
} from '@/modules/shared/components/serviceAccount/ServiceAccountRoster'
import { SubNavigationButton } from '@/modules/shared/components/SubNavigationButton'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { SplitServiceCompanyModal } from './SplitServiceCompanyModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { ServiceAccountAccessPanel } from '@/modules/shared/components/serviceAccount/access/ServiceAccountAccessPanel'

// Error codes specific to the attach/detach-company flows here (the roster codes live in
// ServiceAccountRoster.rosterErrorMessage, which the shared users panel uses internally).
const errorMessage = (code: string | undefined): string => {
  switch (code) {
    case 'TENANT_NOT_SERVICE_COMPANY':
      return 'Wybrane konto nie jest kontem usługowym.'
    case 'CANNOT_ATTACH_SERVICE_COMPANY':
      return 'Nie można dodać konta usługowego pod inne konto usługowe.'
    case 'CANNOT_ATTACH_PARENT_COMPANY':
      return 'Firma ma własne firmy podrzędne — nie można jej dodać pod konto usługowe.'
    case 'CANNOT_ATTACH_TO_SELF':
      return 'Firma nie może być własnym kontem usługowym.'
    case 'COMPANY_HAS_NO_TENANT':
      return 'Firma nie należy do żadnego konta usługowego.'
    case 'TENANT_NOT_FOUND':
      return 'Nie znaleziono konta usługowego.'
    case 'NOT_AUTHORIZED':
      return 'Brak uprawnień do tej operacji.'
    default:
      return 'Operacja nie powiodła się.'
  }
}

export const ServiceAccountTab: React.FC = () => {
  const { t } = useLocalize()
  const { data, isLoading, error, refetch } = useQueryServiceAccounts()
  const [filter, setFilter] = useState('')
  // ?account=<id> — deep link from the company edit screen.
  const [selectedId, setSelectedId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('account'),
  )
  const [onlyWithDatabase, setOnlyWithDatabase] = useState(false)

  const accounts = data ?? []
  const withDatabaseCount = accounts.filter((a) => a.hasDatabase).length

  const filtered = useMemo<ServiceAccount[]>(() => {
    const q = filter.trim().toLowerCase()
    return accounts.filter(
      (a) =>
        (!onlyWithDatabase || a.hasDatabase) &&
        (!q ||
          (a.companyName ?? '').toLowerCase().includes(q) ||
          (a.nip ?? '').toLowerCase().includes(q)),
    )
  }, [accounts, filter, onlyWithDatabase])

  const selected = useMemo<ServiceAccount | null>(
    () => accounts.find((a) => a.id === selectedId) ?? null,
    [accounts, selectedId],
  )

  if (isLoading) {
    return <div className="tw-p-6 tw-text-gray-500">Ładowanie kont usługowych…</div>
  }
  if (error) {
    const status = (error as any)?.response?.status
    const message =
      status === 403
        ? 'Brak uprawnień. Wymagane konto administratora.'
        : 'Nie udało się załadować kont usługowych.'
    return (
      <div className="tw-p-6 tw-text-red-600">
        {message}{' '}
        <button onClick={() => refetch()} className="tw-underline">
          Spróbuj ponownie
        </button>
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <div className="tw-mb-4">
        <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Konta usługowe</h3>
        <p className="tw-text-sm tw-text-gray-600">
          Konto usługowe grupuje firmy rozliczane „parasolowo" — płatność jest sprawdzana po
          NIP konta usługowego dla wszystkich firm pod nim. Wybierz konto, aby dodawać lub
          usuwać firmy. Aby oznaczyć firmę bez bazy jako konto usługowe, użyj pola „Konto
          usługowe" w edycji firmy.
        </p>
      </div>

      <div className="tw-flex tw-gap-6">
        <div className="tw-w-1/3">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtruj po nazwie, NIP…"
            className="tw-mb-3 tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          />
          {/* Stays while the filter is on, or it could never be switched off again. */}
          {(withDatabaseCount > 0 || onlyWithDatabase) && (
            <label className="tw-mb-3 tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700">
              <Checkbox checked={onlyWithDatabase} onChange={setOnlyWithDatabase} />
              {t('serviceAccountSplit.only-to-split', { count: withDatabaseCount })}
            </label>
          )}
          <div className="tw-rounded tw-border tw-border-gray-200 tw-divide-y tw-divide-gray-100">
            {filtered.length === 0 && (
              <div className="tw-py-6 tw-text-center tw-text-gray-500 tw-text-sm">
                Brak kont usługowych.
              </div>
            )}
            {filtered.map((a) => {
              const isActive = a.id === selectedId
              return (
                <button
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  className={`tw-block tw-w-full tw-text-left tw-px-3 tw-py-2 tw-cursor-pointer ${
                    isActive ? 'tw-bg-blue-50' : 'hover:tw-bg-gray-50'
                  }`}
                >
                  <div className="tw-font-medium tw-text-gray-900">{a.companyName ?? '—'}</div>
                  {a.hasDatabase && <ToSplitBadge />}
                  <div className="tw-text-xs tw-text-gray-500">
                    {a.nip ? `NIP: ${a.nip}` : 'brak NIP'} · {billingProviderLabel(a.billingProvider)}
                  </div>
                  <div className="tw-text-xs tw-text-gray-400">
                    {a.children.length} {a.children.length === 1 ? 'firma' : 'firm'}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="tw-flex-1">
          {!selected ? (
            <div className="tw-p-6 tw-text-gray-500 tw-text-sm tw-rounded tw-border tw-border-dashed tw-border-gray-300">
              Wybierz konto usługowe z listy po lewej, aby zarządzać firmami.
            </div>
          ) : (
            <ServiceAccountDetail
              key={selected.id}
              account={selected}
              onSplit={(newAccountId) => {
                setOnlyWithDatabase(false)
                setSelectedId(newAccountId)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

const ToSplitBadge: React.FC = () => {
  const { t } = useLocalize()
  return (
    <span className="tw-my-0.5 tw-inline-block tw-rounded tw-bg-amber-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-amber-800">
      {t('serviceAccountSplit.to-split-badge')}
    </span>
  )
}

const ServiceAccountDetail: React.FC<{
  account: ServiceAccount
  onSplit: (newAccountId: string) => void
}> = ({ account, onSplit }) => {
  const { t } = useLocalize()
  const [actionError, setActionError] = useState<string | null>(null)
  const [tab, setTab] = useState<'companies' | 'users' | 'access'>('companies')
  const [splitOpen, setSplitOpen] = useState(false)

  return (
    <div>
      <div className="tw-mb-3">
        <h4 className="tw-text-base tw-font-semibold tw-text-gray-800">{account.companyName}</h4>
        <div className="tw-text-xs tw-text-gray-500">
          {account.nip ? `NIP: ${account.nip}` : 'brak NIP'} ·{' '}
          {billingProviderLabel(account.billingProvider)}
        </div>
      </div>

      {account.hasDatabase && (
        <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-900">
          <div>
            {t('serviceAccountSplit.has-database-banner')}
          </div>
          <button
            onClick={() => setSplitOpen(true)}
            className="tw-shrink-0 tw-cursor-pointer tw-rounded tw-bg-amber-600 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-amber-700"
          >
            {t('serviceAccountSplit.submit')}
          </button>
        </div>
      )}

      {splitOpen && (
        <SplitServiceCompanyModal
          account={account}
          onClose={() => setSplitOpen(false)}
          onSplit={(newAccountId) => {
            setSplitOpen(false)
            onSplit(newAccountId)
          }}
        />
      )}

      <div className="tw-mb-4 tw-flex tw-gap-1 tw-border-b tw-border-gray-200">
        <SubNavigationButton isActive={tab === 'companies'} onClick={() => setTab('companies')}>
          Firmy ({account.children.length})
        </SubNavigationButton>
        <SubNavigationButton isActive={tab === 'users'} onClick={() => setTab('users')}>
          Użytkownicy
        </SubNavigationButton>
        <SubNavigationButton isActive={tab === 'access'} onClick={() => setTab('access')}>
          Osoby w firmach
        </SubNavigationButton>
      </div>

      {actionError && (
        <div className="tw-mb-3 tw-text-sm tw-bg-red-100 tw-text-red-800 tw-rounded tw-px-3 tw-py-2">
          {actionError}
        </div>
      )}

      {tab === 'access' ? (
        <ServiceAccountAccessPanel account={account} />
      ) : tab === 'users' ? (
        <ServiceAccountUsersPanel account={account} onError={setActionError} allowSearch />
      ) : (
        <ServiceAccountCompaniesPanel account={account} onError={setActionError} />
      )}
    </div>
  )
}

const ServiceAccountCompaniesPanel: React.FC<{
  account: ServiceAccount
  onError: (message: string | null) => void
}> = ({ account, onError }) => {
  const detach = useMutationDetachCompany()
  const confirm = useConfirm()

  const onDetach = async (child: ServiceAccountChild) => {
    const ok = await confirm({
      title: 'Usunąć firmę z konta usługowego?',
      message: `Firma „${child.companyName}" zostanie odłączona od konta „${account.companyName}" i będzie rozliczana na własny NIP.`,
      confirmText: 'Usuń z konta',
      cancelText: 'Anuluj',
    })
    if (!ok) return

    onError(null)
    detach.mutate(
      { companyId: child.id },
      { onError: (e) => onError(errorMessage(extractCode(e))) },
    )
  }

  return (
    <div>
      <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200 tw-mb-5">
        <table className="tw-w-full tw-text-sm">
          <thead className="tw-bg-gray-50 tw-text-gray-600 tw-uppercase tw-text-xs">
            <tr>
              <Th>Firma</Th>
              <Th>NIP</Th>
              <Th className="tw-text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {account.children.length === 0 && (
              <tr>
                <td colSpan={3} className="tw-py-6 tw-text-center tw-text-gray-500">
                  Brak firm pod tym kontem usługowym.
                </td>
              </tr>
            )}
            {account.children.map((c) => (
              <tr key={c.id} className="tw-border-t tw-border-gray-100 hover:tw-bg-gray-50">
                <Td>
                  <div className="tw-font-medium tw-text-gray-900">{c.companyName}</div>
                  <div className="tw-text-xs tw-text-gray-400 tw-font-mono">{c.id}</div>
                </Td>
                <Td>{c.nip ?? '—'}</Td>
                <Td className="tw-text-right tw-whitespace-nowrap">
                  <button
                    onClick={() => onDetach(c)}
                    disabled={detach.isPending}
                    className="tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-red-600 hover:tw-bg-red-700 disabled:tw-opacity-50"
                  >
                    Usuń z konta
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddCompanyControl account={account} onError={onError} />
    </div>
  )
}
const AddCompanyControl: React.FC<{
  account: ServiceAccount
  onError: (message: string | null) => void
}> = ({ account, onError }) => {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useQueryAttachableCompanies(search)
  const attach = useMutationAttachCompany()

  const onAttach = (company: ServiceAccountChild) => {
    onError(null)
    attach.mutate(
      { companyId: company.id, tenantId: account.id },
      { onError: (e) => onError(errorMessage(extractCode(e))) },
    )
  }

  const candidates = data ?? []

  return (
    <div className="tw-rounded tw-border tw-border-gray-200 tw-p-4">
      <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-800">Dodaj firmę</div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Szukaj firmy po nazwie lub NIP…"
        className="tw-mb-3 tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
      />
      <div className="tw-max-h-64 tw-overflow-y-auto tw-rounded tw-border tw-border-gray-100 tw-divide-y tw-divide-gray-100">
        {isLoading && <div className="tw-py-4 tw-text-center tw-text-gray-500 tw-text-sm">Ładowanie…</div>}
        {!isLoading && candidates.length === 0 && (
          <div className="tw-py-4 tw-text-center tw-text-gray-500 tw-text-sm">
            Brak firm do dodania.
          </div>
        )}
        {!isLoading &&
          candidates.map((c) => (
            <div
              key={c.id}
              className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 hover:tw-bg-gray-50"
            >
              <div>
                <div className="tw-text-sm tw-text-gray-900">{c.companyName}</div>
                <div className="tw-text-xs tw-text-gray-500">{c.nip ? `NIP: ${c.nip}` : 'brak NIP'}</div>
              </div>
              <button
                onClick={() => onAttach(c)}
                disabled={attach.isPending}
                className="tw-rounded tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-white tw-cursor-pointer tw-bg-emerald-600 hover:tw-bg-emerald-700 disabled:tw-opacity-50"
              >
                Dodaj
              </button>
            </div>
          ))}
      </div>
    </div>
  )
}
